/**
 * Date formatting and conversion utilities
 */

/**
 * Options for formatting dates
 */
export interface DateFormatOptions {
  /** Include time in output */
  includeTime?: boolean;
  /** Use 24-hour time format */
  use24Hour?: boolean;
  /** Include seconds in time */
  includeSeconds?: boolean;
}

/**
 * Default format options
 */
const defaultOptions: DateFormatOptions = {
  includeTime: true,
  use24Hour: false,
  includeSeconds: false
};

/**
 * Converts a UTC date string or Date object to local time and formats it
 * @param date UTC date string or Date object
 * @param options Formatting options
 * @returns Formatted date string in local time
 */
export function formatDate(date: string | Date | null | undefined, options: DateFormatOptions = {}): string {
  if (!date) return '';
  
  try {
    const mergedOptions = { ...defaultOptions, ...options };
    
    // If date is a string, ensure it's parsed as UTC
    const d = typeof date === 'string' ? new Date(date + 'Z') : date;
    
    if (isNaN(d.getTime())) {
      console.warn('Invalid date:', date);
      return '';
    }

    const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      ...(mergedOptions.includeTime ? {
        hour: 'numeric',
        minute: 'numeric',
        ...(mergedOptions.includeSeconds ? { second: 'numeric' } : {}),
        hour12: !mergedOptions.use24Hour
      } : {})
    });

    return dateTimeFormat.format(d);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Formats a UTC date string as a relative time (e.g. "2 hours ago")
 * @param date UTC date string or Date object
 * @returns Relative time string
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      console.warn('Invalid date:', date);
      return '';
    }

    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return formatDate(date, { includeTime: false });
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '';
  }
}

/**
 * Checks if a date string or Date object is valid
 * @param date Date to validate
 * @returns True if date is valid
 */
export function isValidDate(date: string | Date | null | undefined): boolean {
  if (!date) return false;
  
  try {
    const d = new Date(date);
    return !isNaN(d.getTime());
  } catch {
    return false;
  }
}

/**
 * Gets the current date and time in ISO format
 * @returns Current date/time string
 */
export function getCurrentDateTime(): string {
  return new Date().toISOString();
}

/**
 * Converts a local date to UTC ISO string
 * @param date Local date to convert
 * @returns UTC ISO string
 */
export function toUTCString(date: Date | string): string {
  if (!date) return '';
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      console.warn('Invalid date:', date);
      return '';
    }
    return d.toISOString();
  } catch (error) {
    console.error('Error converting to UTC:', error);
    return '';
  }
}
